import React, { useState, useEffect } from "react";
import { FaBook, FaHeartbeat, FaSignOutAlt } from "react-icons/fa";
import {
  TbChartInfographic,
  TbLayoutGridAdd,
  TbChecklist,
} from "react-icons/tb";
import { BiLogIn } from "react-icons/bi";
import { IoIosArrowDropright, IoIosArrowDropleft } from "react-icons/io";
import Main from "../components/main";
import { Link } from "react-router-dom";
import {
  activeMenu,
  activeStanding,
  activeText,
  activeToggle,
} from "../utils/helper";
import Logo from "../assets/images/normal_logo.png";
import { useAuth } from "../services/authProvider";
import { logoutWithWarning } from "../utils/logoutWithWarning";
import { PERMISSIONS, getActiveRole } from "../utils/permissions";
import usePatientPermissions from "../hooks/usePatientPermissions";

function HCPLayout({ setLocale }) {
  const { logout } = useAuth();
  const [rtl, setRtl] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [image, setImage] = useState(true);
  const [toggled, setToggled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const patient = localStorage.getItem("patient");

  // Fetch permissions reactively so the sidebar re-renders when they arrive
  const { data: permissionsData } = usePatientPermissions();
  const isAdmin = getActiveRole() === 'CLINICAL_LOCATION_ADMIN';
  const livePermissions = permissionsData?.data ?? [];
  const canCreateFunctionalScore = isAdmin || livePermissions.includes(PERMISSIONS.CREATE_FUNCTIONAL_SCORE);
  const canReadFunctionalScore = isAdmin || livePermissions.includes(PERMISSIONS.READ_FUNCTIONAL_SCORE);
  const canReadCarePlan = isAdmin || livePermissions.includes(PERMISSIONS.READ_CAREPLAN);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // Auto-collapse on mobile, expand on desktop
      if (mobile && !collapsed) {
        setCollapsed(true);
      } else if (!mobile && collapsed && !toggled) {
        setCollapsed(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [collapsed, toggled]);

  // Handle initial load
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, []);

  // Handle body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && toggled) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("sidebar-open");
    };
  }, [isMobile, toggled]);

  const handleCollapsedChange = () => {
    if (isMobile) {
      // On mobile, toggle overlay instead of collapse
      setToggled(!toggled);
    } else {
      // On desktop, toggle collapse
      setCollapsed(!collapsed);
    }
  };

  const handleRtlChange = (checked) => {
    setRtl(checked);
    setLocale(checked ? "ar" : "en");
  };

  const handleImageChange = (checked) => {
    setImage(checked);
  };

  const handleToggleSidebar = (value) => {
    setToggled(value);
  };

  // Close mobile sidebar when clicking outside
  const handleOverlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMobile && toggled) {
      setToggled(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && (
        <div
          className={`sidebar-mobile-overlay ${toggled ? "show" : ""}`}
          onClick={handleOverlayClick}
        />
      )}

      <div className="app d-flex">
        <div
          className={`sidebar pb-2 shadow d-flex flex-column ${
            isMobile
              ? `sidebar-mobile ${toggled ? "show" : ""}`
              : `sidebar-desktop ${
                  collapsed ? "sidebar-collapsed" : "sidebar-expanded"
                }`
          }`}
        >
          {/* Mobile Close Button */}
          {isMobile && (
            <button
              className="mobile-menu-close d-md-none"
              onClick={() => setToggled(false)}
            >
              ×
            </button>
          )}

          <ul className="list-group flex-grow-1">
            <div className="logo_img">
              <img
                src={Logo}
                className="img-fluid"
                width={collapsed && !isMobile ? 40 : 119}
                height={collapsed && !isMobile ? 40 : 60}
                alt="Logo"
              />
            </div>
            {/* <li>
              <Link
                to={"/hcp"}
                className={`list-group-item mb-1 border-0 h-40 pt-1 ${activeMenu(
                  "hcp/dashboard"
                )} noHover2`}
                onClick={() => isMobile && setToggled(false)}
              >
                <i
                  className={`fa fa-dashboard icon-small ${
                    activeText("/hcp/dashboard") && "text-white"
                  }`}
                />{" "}
                &nbsp;
                {(!collapsed || isMobile) && (
                  <small
                    className={`f-13   ${
                      activeText("/hcp/dashboard") && "text-white"
                    }`}
                  >
                    Dashboard
                  </small>
                )}
              </Link>
            </li> */}
            <li>
              <Link
                to={"/hcp"}
                className={`list-group-item mb-1 border-0 h-40 pt-1 ${activeMenu(
                  "/hcp/patient"
                )} noHover2`}
                onClick={() => isMobile && setToggled(false)}
              >
                <i
                  className={`fa fa-heartbeat icon-small ${
                    activeText("/hcp/patient") && "text-white"
                  }`}
                />{" "}
                &nbsp;
                {(!collapsed || isMobile) && (
                  <small
                    className={`f-13 ${
                      activeText("/hcp/patient") && "text-white"
                    }`}
                  >
                    Patient Care
                  </small>
                )}
              </Link>
              {(!collapsed || isMobile) && (
                <div
                  className={`ml-3 rounded-right bg_dark ${
                    activeToggle("/hcp/patient", "/hcp/patient/list")
                      ? "d-block"
                      : "d-none"
                  }`}
                >
                  {patient ? (
                    <Link
                      className="noHover3 h-40"
                      to={`/hcp/patient/details/${patient}`}
                      onClick={() => isMobile && setToggled(false)}
                    >
                      <div
                        className={`pl-4 py-2 noHover3 ${
                          activeToggle("/hcp/patient/details")
                            ? "bg_active_header rounded-right"
                            : "text-muted"
                        }`}
                      >
                        <small className="f-11">Patient Home Page</small>
                      </div>
                    </Link>
                  ) : (
                    <div
                      className={`pl-4 py-2 h-40 ${
                        activeToggle("/hcp/patient/details")
                          ? "bg_active_header rounded-right"
                          : ""
                      }`}
                    >
                      <small className="text_small text-muted f-11">
                        Patient Home Page
                      </small>
                    </div>
                  )}
                  {/* <div className="wa">
                    <div className="h-40 py-2">
                      <small className="ml-4 text_small text-muted f-11">
                        Clinical Assessment
                      </small>
                    </div>
                    <Link
                      className="noHover"
                      to={"/hcp/patient/create/assessment_notes"}
                      onClick={() => isMobile && setToggled(false)}
                    >
                      <div
                        className={`py-1 pl-5 noHover h-40 ${
                          activeToggle("/hcp/patient/create/assessment_notes")
                            ? "bg_active_menu rounded-right"
                            : ""
                        }`}
                      >
                        <small className="text_smaller text-muted f-9">
                          Create Assessment Notes
                        </small>
                      </div>
                    </Link>
                    <Link
                      className="noHover"
                      to={"/hcp/patient/assessment_notes"}
                      onClick={() => isMobile && setToggled(false)}
                    >
                      <div
                        className={`py-1 pl-5 noHover h-40 ${
                          activeToggle("/hcp/patient/assessment_notes")
                            ? "bg_active_menu rounded-right"
                            : ""
                        }`}
                      >
                        <small className="text_smaller text-muted f-9">
                          View Assessment Notes
                        </small>
                      </div>
                    </Link>
                  </div> */}
                  {(canCreateFunctionalScore || canReadFunctionalScore) && (
                    <div className="wa">
                      <div className="h-40 py-2">
                        <small className="ml-4 text_small text-muted f-11">
                          Functional Scores
                        </small>
                      </div>
                      {canCreateFunctionalScore && (
                        <Link
                          className="noHover"
                          to={"/hcp/patient/function_score"}
                          onClick={() => isMobile && setToggled(false)}
                        >
                          <div
                            className={`py-1 pl-5 noHover h-40 ${
                              activeToggle("/hcp/patient/function") ||
                              activeToggle("/hcp/review_function_entry")
                                ? "bg_active_menu rounded-right"
                                : ""
                            }`}
                          >
                            <small className="text_smaller text-muted f-9">
                              Enter Functional Scores
                            </small>
                          </div>
                        </Link>
                      )}
                      {canReadFunctionalScore && (
                        <Link
                          className="noHover"
                          to={"/hcp/patient/summary_functions"}
                          onClick={() => isMobile && setToggled(false)}
                        >
                          <div
                            className={`py-1 pl-5 noHover h-40 ${
                              activeToggle(
                                "/hcp/patient/team_score_functional_summaries"
                              )
                                ? "bg_active_menu rounded-right"
                                : ""
                            } ${
                              activeToggle("/hcp/patient/summary_functions") &&
                              "bg_active_menu rounded-right"
                            }`}
                          >
                            <small className="text_smaller text-muted f-9">
                              View Functional Summaries
                            </small>
                          </div>
                        </Link>
                      )}
                    </div>
                  )}
                  <div className="wa">
                    <div className="h-40 py-1">
                      <small className="ml-4 text_small text-muted f-11">
                        Care Management Plan
                      </small>
                    </div>
                    <Link
                      to={"/hcp/patient/care_management"}
                      className="noHover"
                      onClick={() => isMobile && setToggled(false)}
                    >
                      <div
                        className={`py-1 pl-5 noHover h-40 ${
                          activeToggle("/hcp/patient/care")
                            ? "bg_active_menu rounded-right"
                            : ""
                        } `}
                      >
                        <small className="text_smaller text-muted f-9">
                          Create Care Plan
                        </small>
                      </div>
                    </Link>
                      {canReadCarePlan && (
                        <Link
                          to={"/hcp/patient/plan_care_summaries"}
                          className="noHover"
                          onClick={() => isMobile && setToggled(false)}
                        >
                          <div
                            className={`py-1 pl-5 noHover h-40 ${
                              activeToggle("/hcp/patient/plan_care_summaries")
                                ? "bg_active_menu rounded-right"
                                : ""
                            }`}
                          >
                            <small className="text_smaller text-muted f-9">
                              View Care Plan Summaries
                            </small>
                          </div>
                        </Link>
                      )}
                    </div>
                  {/* <Link
                    className="noHover3"
                    to={`/hcp/patient/outstanding_list/`}
                    onClick={() => isMobile && setToggled(false)}
                  >
                    <div
                      className={`pl-4 py-1 noHover3 h-40 ${
                        activeToggle("/hcp/patient/outstanding_list")
                          ? "bg_active_header rounded-right text-white"
                          : "text-muted"
                      }`}
                    >
                      <small className="f-11">Outstanding Documents</small>
                    </div>
                  </Link> */}
                </div>
              )}
            </li>
            <li>
              <Link
                to={"/hcp/outstanding_documents"}
                className={`list-group-item mb-1 border-0 h-40 pt-1 ${activeMenu(
                  "/hcp/outstanding_documents"
                )} noHover2`}
                onClick={() => isMobile && setToggled(false)}
              >
                <i
                  className={`fa fa-book icon-small ${
                    activeText("/hcp/outstanding_documents") && "text-white"
                  }`}
                />{" "}
                &nbsp;
                {(!collapsed || isMobile) && (
                  <small
                    className={`f-13   ${
                      activeText("/hcp/outstanding_documents") && "text-white"
                    }`}
                  >
                    Outstanding Documents
                  </small>
                )}
              </Link>
            </li>
            <li>
              <Link
                to={"/hcp/profile/personal_details"}
                className={`list-group-item my-0 border-0 h-40 pt-1 ${activeMenu(
                  "/hcp/profile"
                )} noHover2`}
                onClick={() => isMobile && setToggled(false)}
              >
                <i
                  className={`fa fa-user icon-small ${
                    activeText("/hcp/profile") && "text-white"
                  }`}
                />{" "}
                &nbsp;
                {(!collapsed || isMobile) && (
                  <small
                    className={`f-13   ${
                      activeText("/hcp/profile") && "text-white"
                    }`}
                  >
                    Profile
                  </small>
                )}
              </Link>
            </li>
          </ul>

          {/* Logout at bottom */}
          <div className="sidebar-logout">
            <div
              className="noHover2 px-4 py-2 d-flex align-items-center cursor-pointer"
              onClick={() => {
                logoutWithWarning(logout);
                if (isMobile) setToggled(false);
              }}
            >
              <FaSignOutAlt /> &nbsp;{" "}
              {(!collapsed || isMobile) && <small>Logout</small>}
            </div>
          </div>
        </div>

        <div
          className={`w-100 ${
            isMobile
              ? "main-content-mobile"
              : collapsed
              ? "main-content-collapsed"
              : "main-content-expanded"
          }`}
        >
          <Main
            handleCollapsedChange={handleCollapsedChange}
            page={
              (window.location.pathname === "/hcp/patient/list" ||
                window.location.pathname === "/hcp/profile/personal_details" ||
                window.location.pathname ===
                  "/hcp/profile/professional_information" ||
                window.location.pathname === "/hcp/profile/change_password" ||
                window.location.pathname === "/hcp/outstanding_documents") &&
              "patient"
            }
          />
        </div>
      </div>
    </>
  );
}

export default HCPLayout;
